// Passions — Level 1 selector data.
// Users pick passions; each passion contributes both:
//  (1) additive boosts to Career DNA archetypes (Builder, Thinker, Advocate,
//      Caregiver, Creator, Organizer, Leader, Adventurer)
//  (2) a list of related Career Families used to award a direct
//      family-fit bonus during career matching.
// Every Career Family present in /public/data/careers.csv is covered by
// at least one passion so that selecting passions surfaces relevant roles.

import type { Archetype } from "@/lib/careerDatabase";

/**
 * The legacy v1 `Archetype` union carries "Adventurer" but not "Connector".
 * v2's canonical eight are the reverse. Passions are shared by both, so the
 * weight keys accept either name; `LEGACY_ARCHETYPE` in
 * features/assessment-v2/passions.ts folds them onto the canonical eight
 * (Adventurer → Builder). CANON-PENDING: the mapping belongs in the
 * Command Center item bank.
 */
export type PassionArchetypeKey = Archetype | "Connector";

export interface Passion {
  id: string;
  label: string;
  description: string;
  category: string;
  /** Additive weights per archetype (capped during scoring). */
  archetypeWeights: Partial<Record<PassionArchetypeKey, number>>;
  /** Career family names this passion directly aligns with. */
  families: string[];
}

export const NONE_PASSION_ID = "none";

export const PASSION_CATEGORIES: string[] = [
  // Causes and real free-time behaviour lead: they are the two categories a
  // 15-year-old recognises themselves in immediately, and the accordion opens
  // on the first one.
  "The World You Want To Fix",
  "How You Spend Your Own Time",
  "Arts & Design",
  "Business, Product & Marketing",
  "Engineering & Technical Skills",
  "Finance, Economics & Legal",
  "Entertainment & Performing Arts",
  "Health, Wellness & Beauty",
  "Languages, Media & Content",
  "Education, Social Services & Coaching",
  "Science, Research & Environment",
  "Sports, Fitness & Mindfulness",
  "Animals, Wildlife & Nature",
  "Travel, Hospitality & Food",
  "Crafts, Making & Hands-on Work",
];

export const PASSIONS: Passion[] = [
  // ── Arts & Design ──────────────────────────────────────────────
  { id: "drawing", label: "Drawing & Illustration", description: "Making art by hand/digital", category: "Arts & Design",
    archetypeWeights: { Creator: 3 },
    families: ["Creative Arts & Media", "Design & User Experience"] },
  { id: "fashion", label: "Fashion Design", description: "Designing clothes and accessories", category: "Arts & Design",
    archetypeWeights: { Creator: 3, Builder: 1 },
    families: ["Creative Arts & Media", "Retail & Consumer Services"] },
  { id: "interior", label: "Interior Design", description: "Designing indoor spaces", category: "Arts & Design",
    archetypeWeights: { Creator: 2, Organizer: 2 },
    families: ["Architecture & Urban Planning", "Creative Arts & Media"] },
  { id: "painting", label: "Painting", description: "Using color on canvas", category: "Arts & Design",
    archetypeWeights: { Creator: 3 },
    families: ["Creative Arts & Media"] },
  { id: "photography", label: "Photography", description: "Shooting and editing photos", category: "Arts & Design",
    archetypeWeights: { Creator: 3, Adventurer: 1 },
    families: ["Creative Arts & Media", "Journalism & Media"] },
  { id: "uxui", label: "UX/UI & Interaction Design", description: "Designing user-friendly digital products", category: "Arts & Design",
    archetypeWeights: { Creator: 2, Thinker: 2 },
    families: ["Design & User Experience", "Technology & Software", "Product Management"] },
  { id: "animation", label: "Animation & Motion Graphics", description: "Creating moving visual designs", category: "Arts & Design",
    archetypeWeights: { Creator: 3, Builder: 1 },
    families: ["Creative Arts & Media", "Design & User Experience"] },

  // ── Business, Product & Marketing ──────────────────────────────
  { id: "ecommerce", label: "E-commerce", description: "Selling goods online", category: "Business, Product & Marketing",
    archetypeWeights: { Leader: 2, Builder: 1, Adventurer: 1 },
    families: ["Retail & Consumer Services", "Marketing & Growth"] },
  { id: "events", label: "Event Planning", description: "Organising events and parties", category: "Business, Product & Marketing",
    archetypeWeights: { Organizer: 3, Leader: 1 },
    families: ["Hospitality & Tourism", "Marketing & Growth"] },
  { id: "marketing", label: "Marketing & Advertising", description: "Promoting products and services", category: "Business, Product & Marketing",
    archetypeWeights: { Creator: 2, Leader: 2 },
    families: ["Marketing & Growth", "Creative Arts & Media"] },
  { id: "realestate", label: "Real Estate", description: "Buying and managing property", category: "Business, Product & Marketing",
    archetypeWeights: { Leader: 2, Organizer: 2 },
    families: ["Real Estate & Construction"] },
  { id: "productmgmt", label: "Product Management", description: "Building and improving products across teams", category: "Business, Product & Marketing",
    archetypeWeights: { Leader: 2, Thinker: 2, Organizer: 1 },
    families: ["Product Management", "Technology & Software"] },
  { id: "projectmgmt", label: "Project Management", description: "Planning and delivering projects", category: "Business, Product & Marketing",
    archetypeWeights: { Organizer: 3, Leader: 1 },
    families: ["Consulting & Strategy", "Human Resources"] },
  { id: "entrepreneurship", label: "Entrepreneurship & Startups", description: "Launching and growing new ventures", category: "Business, Product & Marketing",
    archetypeWeights: { Adventurer: 2, Leader: 2, Creator: 1 },
    families: ["Entrepreneurship & Startups", "Consulting & Strategy"] },
  { id: "sales", label: "Sales & Business Development", description: "Selling and growing business opportunities", category: "Business, Product & Marketing",
    archetypeWeights: { Leader: 3, Adventurer: 1 },
    families: ["Sales & Business Development", "Retail & Consumer Services"] },

  // ── Engineering & Technical Skills ─────────────────────────────
  { id: "aerospace", label: "Aerospace Engineering", description: "Designing aircraft and spacecraft", category: "Engineering & Technical Skills",
    archetypeWeights: { Thinker: 2, Builder: 3 },
    families: ["Space & Aerospace", "Engineering (Non-IT)"] },
  { id: "automotive", label: "Automotive & Mechanics", description: "Repairing and servicing vehicles", category: "Engineering & Technical Skills",
    archetypeWeights: { Builder: 3 },
    families: ["Engineering (Non-IT)", "Manufacturing & Industrial", "Transportation & Logistics", "Electric Mobility & Battery Tech"] },
  { id: "construction", label: "Building & Construction", description: "Constructing buildings and roads", category: "Engineering & Technical Skills",
    archetypeWeights: { Builder: 3, Organizer: 1 },
    families: ["Real Estate & Construction", "Architecture & Urban Planning", "Blue-Collar & Trade"] },
  { id: "chemeng", label: "Chemical Engineering", description: "Making products via chemistry", category: "Engineering & Technical Skills",
    archetypeWeights: { Thinker: 3, Builder: 1 },
    families: ["Engineering (Non-IT)", "Pharma & Biotech", "Manufacturing & Industrial"] },
  { id: "cybersec", label: "Cybersecurity", description: "Protecting computers and data", category: "Engineering & Technical Skills",
    archetypeWeights: { Thinker: 3, Builder: 1 },
    families: ["Technology & Software", "AI & Emerging Technologies"] },
  { id: "electrical", label: "Electrical Engineering", description: "Working with electrical systems", category: "Engineering & Technical Skills",
    archetypeWeights: { Builder: 3, Thinker: 1 },
    families: ["Engineering (Non-IT)", "Semiconductors & Electronics", "Electric Mobility & Battery Tech"] },
  { id: "homerepair", label: "Home Maintenance & Repair", description: "Fixing home systems", category: "Engineering & Technical Skills",
    archetypeWeights: { Builder: 3 },
    families: ["Blue-Collar & Trade", "Informal Sector & Gig Economy"] },
  { id: "mecheng", label: "Mechanical Engineering", description: "Designing machines", category: "Engineering & Technical Skills",
    archetypeWeights: { Builder: 3, Thinker: 1 },
    families: ["Engineering (Non-IT)", "Manufacturing & Industrial"] },
  { id: "coding", label: "Programming & Coding", description: "Writing software", category: "Engineering & Technical Skills",
    archetypeWeights: { Thinker: 3, Builder: 1, Creator: 1 },
    families: ["Technology & Software"] },
  { id: "robotics", label: "Robotics", description: "Building and programming robots", category: "Engineering & Technical Skills",
    archetypeWeights: { Builder: 2, Thinker: 2, Creator: 1 },
    families: ["AI & Emerging Technologies", "Engineering (Non-IT)", "Manufacturing & Industrial"] },
  { id: "softwaredev", label: "Software Development", description: "Building applications and digital systems", category: "Engineering & Technical Skills",
    archetypeWeights: { Builder: 2, Thinker: 2, Creator: 1 },
    families: ["Technology & Software", "Product Management"] },
  { id: "dataai", label: "Data, AI & Analytics", description: "Analysing data and building intelligent systems", category: "Engineering & Technical Skills",
    archetypeWeights: { Thinker: 3, Builder: 1 },
    families: ["Data & Analytics", "AI & Emerging Technologies"] },

  // ── Finance, Economics & Legal ─────────────────────────────────
  { id: "banking", label: "Banking", description: "Managing money and loans", category: "Finance, Economics & Legal",
    archetypeWeights: { Organizer: 2, Thinker: 2 },
    families: ["Finance & Accounting"] },
  { id: "economics", label: "Economics & Policy", description: "Studying economies and policy", category: "Finance, Economics & Legal",
    archetypeWeights: { Thinker: 3, Advocate: 1 },
    families: ["Government & Public Sector", "Consulting & Strategy"] },
  { id: "fintech", label: "FinTech", description: "Modernising finance with tech", category: "Finance, Economics & Legal",
    archetypeWeights: { Thinker: 2, Builder: 2, Adventurer: 1 },
    families: ["FinTech & Digital Finance", "Technology & Software"] },
  { id: "insurance", label: "Insurance", description: "Protecting against risks", category: "Finance, Economics & Legal",
    archetypeWeights: { Organizer: 3, Thinker: 1 },
    families: ["Finance & Accounting"] },
  { id: "investing", label: "Investment & Stock Markets", description: "Growing money through investing", category: "Finance, Economics & Legal",
    archetypeWeights: { Thinker: 3, Adventurer: 1 },
    families: ["Finance & Accounting", "FinTech & Digital Finance"] },
  { id: "personalfinance", label: "Personal Finance & Financial Planning", description: "Managing personal wealth and goals", category: "Finance, Economics & Legal",
    archetypeWeights: { Organizer: 2, Thinker: 2, Advocate: 1 },
    families: ["Finance & Accounting"] },
  { id: "legal", label: "Legal & Compliance", description: "Understanding laws, regulations and contracts", category: "Finance, Economics & Legal",
    archetypeWeights: { Thinker: 2, Advocate: 2, Organizer: 1 },
    families: ["Legal & Compliance", "Government & Public Sector"] },

  // ── Entertainment & Performing Arts ────────────────────────────
  { id: "acting", label: "Acting & Drama", description: "Acting on stage or film", category: "Entertainment & Performing Arts",
    archetypeWeights: { Creator: 3, Leader: 1 },
    families: ["Creative Arts & Media"] },
  { id: "comedy", label: "Comedy & Stand-up", description: "Performing comedy", category: "Entertainment & Performing Arts",
    archetypeWeights: { Creator: 2, Leader: 2 },
    families: ["Creative Arts & Media"] },
  { id: "dance", label: "Dance", description: "Expressing through movement", category: "Entertainment & Performing Arts",
    archetypeWeights: { Creator: 3 },
    families: ["Creative Arts & Media", "Sports & Fitness"] },
  { id: "gamedev", label: "Game Design & Development", description: "Creating video games", category: "Entertainment & Performing Arts",
    archetypeWeights: { Creator: 2, Builder: 2, Thinker: 1 },
    families: ["Creative Arts & Media", "Technology & Software", "Design & User Experience"] },
  { id: "music", label: "Music", description: "Performing or composing music", category: "Entertainment & Performing Arts",
    archetypeWeights: { Creator: 3 },
    families: ["Creative Arts & Media"] },
  { id: "musicprod", label: "Music Production", description: "Producing and recording music", category: "Entertainment & Performing Arts",
    archetypeWeights: { Creator: 3, Builder: 1 },
    families: ["Creative Arts & Media"] },
  { id: "voice", label: "Voice Acting & Dubbing", description: "Voicing characters", category: "Entertainment & Performing Arts",
    archetypeWeights: { Creator: 3 },
    families: ["Creative Arts & Media"] },

  // ── Health, Wellness & Beauty ──────────────────────────────────
  { id: "beauty", label: "Beauty & Grooming", description: "Hair, makeup and skincare", category: "Health, Wellness & Beauty",
    archetypeWeights: { Creator: 2, Caregiver: 1 },
    families: ["Hospitality & Tourism", "Retail & Consumer Services", "Informal Sector & Gig Economy"] },
  { id: "medicine", label: "Medicine & Healthcare", description: "Treating and caring for health", category: "Health, Wellness & Beauty",
    archetypeWeights: { Caregiver: 3, Thinker: 1 },
    families: ["Healthcare & Medicine"] },
  { id: "mentalhealth", label: "Mental Health & Counselling", description: "Supporting emotional wellbeing", category: "Health, Wellness & Beauty",
    archetypeWeights: { Caregiver: 3, Advocate: 1 },
    families: ["Healthcare & Medicine", "Social Work & NGO"] },
  { id: "nutrition", label: "Nutrition & Dietetics", description: "Guiding healthy eating", category: "Health, Wellness & Beauty",
    archetypeWeights: { Caregiver: 2, Organizer: 1 },
    families: ["Healthcare & Medicine", "Sports & Fitness"] },
  { id: "spa", label: "Spa & Wellness", description: "Massages and relaxation", category: "Health, Wellness & Beauty",
    archetypeWeights: { Caregiver: 3 },
    families: ["Hospitality & Tourism", "Healthcare & Medicine"] },
  { id: "sportsmed", label: "Sports Medicine", description: "Treating sports injuries", category: "Health, Wellness & Beauty",
    archetypeWeights: { Caregiver: 2, Builder: 1 },
    families: ["Healthcare & Medicine", "Sports & Fitness"] },
  { id: "altmed", label: "Traditional & Alternative Medicine", description: "Healing with traditional methods", category: "Health, Wellness & Beauty",
    archetypeWeights: { Caregiver: 2, Advocate: 1 },
    families: ["Healthcare & Medicine", "India-Specific Roles"] },

  // ── Languages, Media & Content ─────────────────────────────────
  { id: "blogging", label: "Blogging & Content Writing", description: "Writing online content", category: "Languages, Media & Content",
    archetypeWeights: { Creator: 3, Advocate: 1 },
    families: ["Journalism & Media", "Marketing & Growth"] },
  { id: "creativewriting", label: "Creative Writing", description: "Writing stories and scripts", category: "Languages, Media & Content",
    archetypeWeights: { Creator: 3 },
    families: ["Creative Arts & Media", "Journalism & Media"] },
  { id: "journalism", label: "Journalism", description: "Reporting factual news", category: "Languages, Media & Content",
    archetypeWeights: { Advocate: 2, Creator: 1, Adventurer: 1 },
    families: ["Journalism & Media"] },
  { id: "library", label: "Library & Information Services", description: "Organising knowledge resources", category: "Languages, Media & Content",
    archetypeWeights: { Organizer: 3 },
    families: ["Education & Training", "Government & Public Sector"] },
  { id: "linguistics", label: "Linguistics", description: "Studying language", category: "Languages, Media & Content",
    archetypeWeights: { Thinker: 3 },
    families: ["Education & Training", "Science & Research"] },
  { id: "pr", label: "Public Relations & Outreach", description: "Shaping public image", category: "Languages, Media & Content",
    archetypeWeights: { Leader: 2, Advocate: 1 },
    families: ["Marketing & Growth", "Journalism & Media"] },
  { id: "publishing", label: "Publishing", description: "Editing and producing books", category: "Languages, Media & Content",
    archetypeWeights: { Organizer: 2, Creator: 2 },
    families: ["Journalism & Media", "Creative Arts & Media"] },
  { id: "podcasting", label: "Radio & Podcasting", description: "Hosting audio shows", category: "Languages, Media & Content",
    archetypeWeights: { Creator: 2, Leader: 2 },
    families: ["Journalism & Media", "Creative Arts & Media"] },
  { id: "podcastprod", label: "Podcast Production", description: "Producing and editing podcasts", category: "Languages, Media & Content",
    archetypeWeights: { Creator: 2, Builder: 1, Organizer: 1 },
    families: ["Journalism & Media", "Creative Arts & Media"] },
  { id: "teachlang", label: "Teaching Languages", description: "Teaching new languages", category: "Languages, Media & Content",
    archetypeWeights: { Advocate: 2, Caregiver: 1 },
    families: ["Education & Training"] },
  { id: "video", label: "Video Creation & Editing", description: "Producing and editing videos", category: "Languages, Media & Content",
    archetypeWeights: { Creator: 3 },
    families: ["Creative Arts & Media", "Journalism & Media"] },

  // ── Education, Social Services & Coaching ──────────────────────
  { id: "childdev", label: "Child & Family Development", description: "Supporting child and family growth", category: "Education, Social Services & Coaching",
    archetypeWeights: { Caregiver: 3, Advocate: 1 },
    families: ["Social Work & NGO", "Education & Training", "Healthcare & Medicine"] },
  { id: "humanrights", label: "Human Rights & Advocacy", description: "Protecting rights and equality", category: "Education, Social Services & Coaching",
    archetypeWeights: { Advocate: 3, Leader: 1 },
    families: ["Social Work & NGO", "Legal & Compliance", "Government & Public Sector"] },
  { id: "publicpolicy", label: "Public Policy & Governance", description: "Developing public policies", category: "Education, Social Services & Coaching",
    archetypeWeights: { Advocate: 2, Thinker: 2 },
    families: ["Government & Public Sector"] },
  { id: "socialwork", label: "Social Work & Welfare", description: "Supporting communities", category: "Education, Social Services & Coaching",
    archetypeWeights: { Caregiver: 2, Advocate: 2 },
    families: ["Social Work & NGO"] },
  { id: "teaching", label: "Teaching & Education", description: "Teaching and tutoring", category: "Education, Social Services & Coaching",
    archetypeWeights: { Advocate: 2, Caregiver: 2 },
    families: ["Education & Training"] },
  { id: "edtech", label: "Educational Technology (EdTech)", description: "Using technology to improve learning", category: "Education, Social Services & Coaching",
    archetypeWeights: { Builder: 2, Thinker: 2, Advocate: 1 },
    families: ["Education & Training", "Technology & Software"] },
  { id: "coaching", label: "Coaching & Professional Development", description: "Guiding careers and leadership", category: "Education, Social Services & Coaching",
    archetypeWeights: { Leader: 2, Advocate: 2, Caregiver: 1 },
    families: ["Human Resources", "Education & Training", "Consulting & Strategy"] },

  // ── Science, Research & Environment ────────────────────────────
  { id: "biology", label: "Biology & Life Sciences", description: "Studying living things", category: "Science, Research & Environment",
    archetypeWeights: { Thinker: 3 },
    families: ["Science & Research", "Pharma & Biotech", "Healthcare & Medicine"] },
  { id: "chemistry", label: "Chemistry", description: "Studying substances and reactions", category: "Science, Research & Environment",
    archetypeWeights: { Thinker: 3 },
    families: ["Science & Research", "Pharma & Biotech"] },
  { id: "earthsci", label: "Earth & Environmental Science", description: "Studying Earth's systems", category: "Science, Research & Environment",
    archetypeWeights: { Thinker: 2, Advocate: 1, Adventurer: 1 },
    families: ["Energy & Environment", "Green Tech & Climate Action", "Science & Research"] },
  { id: "physics", label: "Physics", description: "Studying matter and energy", category: "Science, Research & Environment",
    archetypeWeights: { Thinker: 3 },
    families: ["Science & Research", "Space & Aerospace"] },
  { id: "astronomy", label: "Space Science & Astronomy", description: "Studying the universe", category: "Science, Research & Environment",
    archetypeWeights: { Thinker: 3, Adventurer: 1 },
    families: ["Space & Aerospace", "Science & Research"] },
  { id: "sustainability", label: "Sustainability & Climate Action", description: "Protecting the environment and climate", category: "Science, Research & Environment",
    archetypeWeights: { Advocate: 2, Thinker: 1, Organizer: 1 },
    families: ["Green Tech & Climate Action", "Energy & Environment"] },

  // ── Sports, Fitness & Mindfulness ──────────────────────────────
  { id: "adventure", label: "Adventure Sports", description: "Outdoor thrill activities", category: "Sports, Fitness & Mindfulness",
    archetypeWeights: { Adventurer: 3 },
    families: ["Sports & Fitness", "Military & Defense"] },
  { id: "fitness", label: "Fitness & Bodybuilding", description: "Building strength and fitness", category: "Sports, Fitness & Mindfulness",
    archetypeWeights: { Builder: 1, Adventurer: 1, Organizer: 1 },
    families: ["Sports & Fitness"] },
  { id: "martialarts", label: "Martial Arts", description: "Training self-defence disciplines", category: "Sports, Fitness & Mindfulness",
    archetypeWeights: { Adventurer: 2, Organizer: 1 },
    families: ["Sports & Fitness", "Military & Defense"] },
  { id: "athletics", label: "Sports & Athletics", description: "Competing in sports", category: "Sports, Fitness & Mindfulness",
    archetypeWeights: { Adventurer: 2, Leader: 1 },
    families: ["Sports & Fitness"] },
  { id: "yoga", label: "Yoga & Meditation", description: "Practising yoga and mindfulness", category: "Sports, Fitness & Mindfulness",
    archetypeWeights: { Caregiver: 2, Organizer: 1 },
    families: ["Sports & Fitness", "Healthcare & Medicine", "India-Specific Roles"] },

  // ── Animals, Wildlife & Nature ─────────────────────────────────
  { id: "animalcare", label: "Animal Care & Welfare", description: "Caring for pets and domestic animals", category: "Animals, Wildlife & Nature",
    archetypeWeights: { Caregiver: 3, Advocate: 1 },
    families: ["Healthcare & Medicine", "Agriculture & Food Production"] },
  { id: "wildlifecons", label: "Wildlife & Conservation", description: "Protecting wild animals and habitats", category: "Animals, Wildlife & Nature",
    archetypeWeights: { Advocate: 2, Adventurer: 2 },
    families: ["Green Tech & Climate Action", "Energy & Environment", "Social Work & NGO"] },
  { id: "vetscience", label: "Veterinary Science Interest", description: "Understanding animal health", category: "Animals, Wildlife & Nature",
    archetypeWeights: { Caregiver: 2, Thinker: 2 },
    families: ["Healthcare & Medicine", "Agriculture & Food Production"] },
  { id: "ecotourism", label: "Wildlife & Ecotourism", description: "Exploring and protecting wildlife", category: "Animals, Wildlife & Nature",
    archetypeWeights: { Adventurer: 2, Advocate: 2 },
    families: ["Hospitality & Tourism", "Green Tech & Climate Action"] },

  // ── Travel, Hospitality & Food ─────────────────────────────────
  { id: "baking", label: "Baking & Pastry", description: "Making breads and desserts", category: "Travel, Hospitality & Food",
    archetypeWeights: { Creator: 2, Builder: 1 },
    families: ["Hospitality & Tourism", "Retail & Consumer Services"] },
  { id: "culinary", label: "Culinary Arts & Food Services", description: "Cooking and serving food", category: "Travel, Hospitality & Food",
    archetypeWeights: { Creator: 2, Caregiver: 1 },
    families: ["Hospitality & Tourism"] },
  { id: "culture", label: "Cultural Studies & Heritage", description: "Studying cultures and history", category: "Travel, Hospitality & Food",
    archetypeWeights: { Advocate: 2, Thinker: 1 },
    families: ["India-Specific Roles", "Education & Training", "Hospitality & Tourism"] },
  { id: "hospitality", label: "Hospitality Management", description: "Running hotels and restaurants", category: "Travel, Hospitality & Food",
    archetypeWeights: { Leader: 2, Organizer: 2 },
    families: ["Hospitality & Tourism"] },
  { id: "tourguide", label: "Tour Guiding", description: "Leading and informing visitors", category: "Travel, Hospitality & Food",
    archetypeWeights: { Adventurer: 2, Advocate: 1 },
    families: ["Hospitality & Tourism", "India-Specific Roles"] },
  { id: "travelwriting", label: "Travel Writing & Blogging", description: "Writing about travel", category: "Travel, Hospitality & Food",
    archetypeWeights: { Creator: 2, Adventurer: 2 },
    families: ["Journalism & Media", "Hospitality & Tourism"] },

  // ── Crafts, Making & Hands-on Work ─────────────────────────────
  { id: "crafts", label: "Crafts & DIY", description: "Creating handmade items and projects", category: "Crafts, Making & Hands-on Work",
    archetypeWeights: { Creator: 2, Builder: 2 },
    families: ["Creative Arts & Media", "Blue-Collar & Trade", "Informal Sector & Gig Economy"] },
  { id: "woodmetal", label: "Woodworking & Metalworking", description: "Building with wood and metal", category: "Crafts, Making & Hands-on Work",
    archetypeWeights: { Builder: 3, Creator: 1 },
    families: ["Blue-Collar & Trade", "Manufacturing & Industrial"] },
  { id: "gardening", label: "Gardening & Urban Farming", description: "Growing plants and food", category: "Crafts, Making & Hands-on Work",
    archetypeWeights: { Caregiver: 1, Builder: 1, Advocate: 1 },
    families: ["Agriculture & Food Production", "Regional & Rural Specific", "Green Tech & Climate Action"] },

  // ── The World You Want To Fix ──────────────────────────────────
  // The 24-area purpose catalogue. Groups and examples: features/assessment-v2/causeCatalogue.ts
  { id: "poverty", label: "A life without poverty", description: "Access to essentials, financial security and fair financial services", category: "The World You Want To Fix",
    archetypeWeights: { Advocate: 2, Organizer: 1 },
    families: ["Social Work & NGO", "FinTech & Digital Finance", "Government & Public Sector"] },
  { id: "foodsecurity", label: "Enough healthy food for everyone", description: "Hunger, nutrition, sustainable farming and less food waste", category: "The World You Want To Fix",
    archetypeWeights: { Advocate: 2, Builder: 1, Organizer: 1 },
    families: ["Agriculture & Food Production", "Green Tech & Climate Action", "Social Work & NGO"] },
  { id: "healthaccess", label: "Healthcare everyone can access", description: "Affordable care, prevention, medicines and treatment people can reach", category: "The World You Want To Fix",
    archetypeWeights: { Caregiver: 3, Advocate: 1 },
    families: ["Healthcare & Medicine", "Pharma & Biotech", "Social Work & NGO"] },
  { id: "wellbeing", label: "Better mental health and well-being", description: "Emotional well-being, easier support and less stigma", category: "The World You Want To Fix",
    archetypeWeights: { Caregiver: 3, Connector: 1 },
    families: ["Healthcare & Medicine", "Social Work & NGO", "Education & Training"] },
  { id: "educationaccess", label: "A fair chance to learn", description: "Education access, literacy, learning differences and lifelong learning", category: "The World You Want To Fix",
    archetypeWeights: { Caregiver: 2, Advocate: 2 },
    families: ["Education & Training", "Social Work & NGO", "Government & Public Sector"] },
  { id: "fairwork", label: "Fair jobs and better livelihoods", description: "Decent work, safe workplaces, fair pay and small-business opportunity", category: "The World You Want To Fix",
    archetypeWeights: { Organizer: 2, Advocate: 1, Leader: 1 },
    families: ["Human Resources", "Entrepreneurship & Startups", "Informal Sector & Gig Economy"] },
  { id: "equalrights", label: "Equal rights and opportunities", description: "Gender equality, fair treatment and including people who are left out", category: "The World You Want To Fix",
    archetypeWeights: { Advocate: 3, Connector: 1 },
    families: ["Legal & Compliance", "Social Work & NGO", "Journalism & Media"] },
  { id: "accessibility", label: "An accessible world for people with disabilities", description: "Accessible places, inclusive learning and assistive tools", category: "The World You Want To Fix",
    archetypeWeights: { Builder: 2, Caregiver: 2 },
    families: ["Design & User Experience", "Healthcare & Medicine", "Technology & Software"] },
  { id: "childrenelders", label: "Better lives for children and older people", description: "Child well-being, dignity in ageing and support for carers", category: "The World You Want To Fix",
    archetypeWeights: { Caregiver: 3, Organizer: 1 },
    families: ["Education & Training", "Healthcare & Medicine", "Social Work & NGO"] },
  { id: "community", label: "Stronger communities and belonging", description: "Less loneliness, more connection between people and generations", category: "The World You Want To Fix",
    archetypeWeights: { Connector: 3, Caregiver: 1 },
    families: ["Social Work & NGO", "Sports & Fitness", "Hospitality & Tourism"] },
  { id: "climate", label: "Climate action and resilience", description: "Cutting emissions and helping communities adapt", category: "The World You Want To Fix",
    archetypeWeights: { Advocate: 3, Thinker: 1 },
    families: ["Green Tech & Climate Action", "Energy & Environment", "Science & Research"] },
  { id: "cleanenergy", label: "Clean energy everyone can afford", description: "Renewable power, energy access and using energy well", category: "The World You Want To Fix",
    archetypeWeights: { Builder: 2, Thinker: 2 },
    families: ["Energy & Environment", "Electric Mobility & Battery Tech", "Engineering (Non-IT)"] },
  { id: "cleanwater", label: "Clean water and safe sanitation", description: "Safe drinking water, sanitation, hygiene and saving water", category: "The World You Want To Fix",
    archetypeWeights: { Builder: 2, Thinker: 1, Caregiver: 1 },
    families: ["Engineering (Non-IT)", "Energy & Environment", "Healthcare & Medicine"] },
  { id: "oceans", label: "Protect oceans and marine life", description: "Marine ecosystems, cleaner seas and coastal habitats", category: "The World You Want To Fix",
    archetypeWeights: { Advocate: 2, Thinker: 2 },
    families: ["Green Tech & Climate Action", "Science & Research", "Energy & Environment"] },
  { id: "conservation", label: "Protect wildlife, forests and nature", description: "Biodiversity, forests, habitats and restoring nature", category: "The World You Want To Fix",
    archetypeWeights: { Advocate: 3, Caregiver: 1 },
    families: ["Green Tech & Climate Action", "Science & Research", "Government & Public Sector"] },
  { id: "animalwelfare", label: "Better lives for animals", description: "Humane treatment, responsible care and living alongside animals", category: "The World You Want To Fix",
    archetypeWeights: { Caregiver: 3, Advocate: 1 },
    families: ["Healthcare & Medicine", "Social Work & NGO", "Science & Research"] },
  { id: "wastecircular", label: "Less waste and pollution", description: "Cleaner air, fewer plastics, repair, reuse and recycling", category: "The World You Want To Fix",
    archetypeWeights: { Creator: 2, Advocate: 2 },
    families: ["Green Tech & Climate Action", "Manufacturing & Industrial", "Design & User Experience"] },
  { id: "citiesfuture", label: "Safe homes and better places to live", description: "Affordable housing, accessible transport and safer public spaces", category: "The World You Want To Fix",
    archetypeWeights: { Organizer: 2, Builder: 2 },
    families: ["Architecture & Urban Planning", "Real Estate & Construction", "Transportation & Logistics"] },
  { id: "peacejustice", label: "Peace, safety and fair justice", description: "Preventing harm, access to justice and accountable institutions", category: "The World You Want To Fix",
    archetypeWeights: { Advocate: 3, Thinker: 1 },
    families: ["Legal & Compliance", "Government & Public Sector", "Social Work & NGO"] },
  { id: "disasterrelief", label: "Support people displaced by conflict or disaster", description: "Emergency help, rebuilding lives and welcoming people to new places", category: "The World You Want To Fix",
    archetypeWeights: { Caregiver: 2, Organizer: 1, Leader: 1 },
    families: ["Social Work & NGO", "Government & Public Sector", "Transportation & Logistics"] },
  { id: "techforall", label: "Technology that helps everyone", description: "Responsible AI, digital inclusion, privacy and fair digital services", category: "The World You Want To Fix",
    archetypeWeights: { Builder: 2, Advocate: 2 },
    families: ["AI & Emerging Technologies", "Technology & Software", "Design & User Experience"] },
  { id: "onlinesafety", label: "A safer internet", description: "Cybersecurity, fewer scams and kinder online spaces", category: "The World You Want To Fix",
    archetypeWeights: { Advocate: 2, Thinker: 2 },
    families: ["Technology & Software", "Legal & Compliance", "Government & Public Sector"] },
  { id: "trustedinfo", label: "Information people can trust", description: "Fact-checking, media literacy and clear public information", category: "The World You Want To Fix",
    archetypeWeights: { Thinker: 2, Creator: 1, Advocate: 1 },
    families: ["Journalism & Media", "Data & Analytics", "Education & Training"] },
  { id: "heritage", label: "Keep cultures, languages and heritage alive", description: "Local languages, traditions and cultural expression", category: "The World You Want To Fix",
    archetypeWeights: { Creator: 3, Connector: 1 },
    families: ["Creative Arts & Media", "Education & Training", "Journalism & Media"] },

  // Space and AI as fields are interests, not problems. Ids kept for saved runs.
  { id: "spaceexploration", label: "Space Exploration", description: "Satellites, rockets and what's out there", category: "Science, Research & Environment",
    archetypeWeights: { Thinker: 3, Adventurer: 1 },
    families: ["Space & Aerospace", "Science & Research", "Semiconductors & Electronics"] },
  { id: "aifuture", label: "Artificial Intelligence", description: "How AI works and what it could do next", category: "Engineering & Technical Skills",
    archetypeWeights: { Thinker: 3, Builder: 1 },
    families: ["AI & Emerging Technologies", "Data & Analytics", "Science & Research"] },

  // ── How You Spend Your Own Time ────────────────────────────────
  // What people actually do on a free evening in 2026. Deliberately
  // specific: "content creation" is a real signal, "being creative" is not.
  { id: "shortvideo", label: "Making Reels & Short Video", description: "Filming, cutting and posting your own videos", category: "How You Spend Your Own Time",
    archetypeWeights: { Creator: 3, Leader: 1 },
    families: ["Creator Economy & Digital", "Creative Arts & Media", "Marketing & Growth", "Journalism & Media"] },
  { id: "gamingesports", label: "Gaming & Esports", description: "Playing seriously, ranking up, following the scene", category: "How You Spend Your Own Time",
    archetypeWeights: { Thinker: 2, Builder: 1, Leader: 1 },
    families: ["Technology & Software", "Creative Arts & Media", "Sports & Fitness"] },
  { id: "streaming", label: "Streaming & Building An Audience", description: "Going live, growing a community around you", category: "How You Spend Your Own Time",
    archetypeWeights: { Creator: 2, Connector: 2 },
    families: ["Creator Economy & Digital", "Creative Arts & Media", "Marketing & Growth", "Entrepreneurship & Startups"] },
  { id: "musicproduction", label: "Music Production & DJing", description: "Making beats, mixing, producing tracks", category: "How You Spend Your Own Time",
    archetypeWeights: { Creator: 3, Thinker: 1 },
    families: ["Creative Arts & Media", "Technology & Software"] },
  { id: "animemanga", label: "Anime, Manga & Comics", description: "Following the stories, and often drawing your own", category: "How You Spend Your Own Time",
    archetypeWeights: { Creator: 2, Thinker: 1 },
    families: ["Creative Arts & Media", "Design & User Experience"] },
  { id: "fandom", label: "Fandom & Online Communities", description: "Running, moderating or organising a community", category: "How You Spend Your Own Time",
    archetypeWeights: { Connector: 3, Organizer: 1 },
    families: ["Marketing & Growth", "Creative Arts & Media", "Human Resources"] },
  { id: "thrifting", label: "Thrifting & Upcycling Clothes", description: "Finding, remaking and styling second-hand", category: "How You Spend Your Own Time",
    archetypeWeights: { Creator: 2, Advocate: 1, Builder: 1 },
    families: ["Retail & Consumer Services", "Creative Arts & Media", "Green Tech & Climate Action"] },
  { id: "skincarebeauty", label: "Skincare & Beauty", description: "Ingredients, routines, doing looks for people", category: "How You Spend Your Own Time",
    archetypeWeights: { Caregiver: 2, Creator: 2 },
    families: ["Retail & Consumer Services", "Healthcare & Medicine", "Creative Arts & Media"] },
  { id: "gymtraining", label: "Gym & Training", description: "Lifting, running, tracking your own progress", category: "How You Spend Your Own Time",
    archetypeWeights: { Builder: 2, Organizer: 2 },
    families: ["Sports & Fitness", "Healthcare & Medicine"] },
  { id: "boardsports", label: "Skating, Surfing & Board Sports", description: "Learning tricks outdoors, falling, going again", category: "How You Spend Your Own Time",
    archetypeWeights: { Adventurer: 3, Builder: 1 },
    families: ["Sports & Fitness", "Creative Arts & Media"] },
  { id: "sidehustle", label: "Running A Small Side Hustle", description: "Selling something you make or resell, for real money", category: "How You Spend Your Own Time",
    archetypeWeights: { Leader: 2, Builder: 2 },
    families: ["Entrepreneurship & Startups", "Retail & Consumer Services", "Sales & Business Development"] },
  { id: "investingmoney", label: "Investing & Personal Finance", description: "Following markets, budgeting, growing savings", category: "How You Spend Your Own Time",
    archetypeWeights: { Thinker: 2, Organizer: 2 },
    families: ["Finance & Accounting", "FinTech & Digital Finance", "Data & Analytics"] },
  { id: "aitools", label: "Playing With AI Tools", description: "Prompting, building small AI things for fun", category: "How You Spend Your Own Time",
    archetypeWeights: { Thinker: 2, Creator: 2 },
    families: ["AI & Emerging Technologies", "Technology & Software", "Design & User Experience"] },
  { id: "sidesoftware", label: "Building Apps & Side Projects", description: "Shipping small tools, sites or bots of your own", category: "How You Spend Your Own Time",
    archetypeWeights: { Builder: 2, Thinker: 2 },
    families: ["Technology & Software", "Product Management", "Entrepreneurship & Startups"] },
  { id: "volunteering", label: "Volunteering", description: "Giving weekends to a cause or a group near you", category: "How You Spend Your Own Time",
    archetypeWeights: { Advocate: 2, Caregiver: 2 },
    families: ["Social Work & NGO", "Education & Training", "Government & Public Sector"] },
  { id: "cookingcontent", label: "Cooking For People", description: "Feeding friends and family, trying recipes", category: "How You Spend Your Own Time",
    archetypeWeights: { Caregiver: 2, Creator: 2 },
    families: ["Hospitality & Tourism", "Agriculture & Food Production"] },
  { id: "plantcare", label: "Plants & Pets", description: "Looking after living things day after day", category: "How You Spend Your Own Time",
    archetypeWeights: { Caregiver: 3, Organizer: 1 },
    families: ["Agriculture & Food Production", "Healthcare & Medicine", "Green Tech & Climate Action"] },
  { id: "tabletop", label: "Tabletop Games & D&D", description: "Strategy, rules, running the story for others", category: "How You Spend Your Own Time",
    archetypeWeights: { Thinker: 2, Leader: 1, Creator: 1 },
    families: ["Creative Arts & Media", "Technology & Software", "Education & Training"] },
  { id: "languagelearning", label: "Learning Languages", description: "Picking up new languages, keeping the streak", category: "How You Spend Your Own Time",
    archetypeWeights: { Thinker: 2, Connector: 2 },
    families: ["Education & Training", "Hospitality & Tourism", "Journalism & Media"] },
];

export const PASSIONS_BY_CATEGORY: Record<string, Passion[]> = PASSION_CATEGORIES.reduce(
  (acc, cat) => {
    acc[cat] = PASSIONS.filter((p) => p.category === cat);
    return acc;
  },
  {} as Record<string, Passion[]>,
);

export function getPassionsByIds(ids: string[]): Passion[] {
  const set = new Set(ids);
  return PASSIONS.filter((p) => set.has(p.id));
}

/**
 * Convert selected passion IDs into per-archetype additive boosts.
 * Caps total boost per archetype at 6 so passions enrich but don't drown
 * out psychometric trait scores.
 */
export function passionsToArchetypeBoost(
  ids: string[],
): Partial<Record<Archetype, number>> {
  if (!ids?.length || (ids.length === 1 && ids[0] === NONE_PASSION_ID)) return {};
  const boost: Record<string, number> = {};
  for (const p of getPassionsByIds(ids)) {
    for (const [arch, w] of Object.entries(p.archetypeWeights)) {
      boost[arch] = Math.min(6, (boost[arch] ?? 0) + (w ?? 0));
    }
  }
  return boost as Partial<Record<Archetype, number>>;
}

/**
 * Convert selected passion IDs into per-Career-Family boost weights.
 * Each passion contributes weight 1 to each of its listed families;
 * weights are capped at 3 per family so 3 overlapping passions ≈ max bonus.
 */
export function passionsToFamilyBoost(ids: string[]): Record<string, number> {
  if (!ids?.length || (ids.length === 1 && ids[0] === NONE_PASSION_ID)) return {};
  const fb: Record<string, number> = {};
  for (const p of getPassionsByIds(ids)) {
    for (const fam of p.families) {
      fb[fam] = Math.min(3, (fb[fam] ?? 0) + 1);
    }
  }
  return fb;
}
